import { Room, RoomEvent, Track } from 'livekit-client';
import axiosInstance from '@/utils/axios';

const activeRooms = new Set();
const activeAudioListeners = new Map();

/**
 * Cleanly detach and remove any audio elements associated with LiveKit
 */
function cleanupAudioElements(roomName = null) {
  const selector = roomName
    ? `audio[data-livekit-room="${roomName}"]`
    : 'audio[data-livekit-audio="true"]';
  document.querySelectorAll(selector).forEach((el) => {
    try {
      el.pause();
      el.srcObject = null;
      el.remove();
    } catch (e) {}
  });
}

export async function connectToLiveKitRoom(roomName, participantName = 'Staff Member', onParticipantConnected = null) {
  try {
    // If there is any existing room for this or another call, cleanly tear it down first
    for (const active of activeRooms) {
      if (active && active.name === roomName) {
        await disconnectLiveKitRoom(active).catch(() => {});
      }
    }

    cleanupAudioElements(roomName);

    const response = await axiosInstance.post('/api/calls/token', {
      roomName,
      participantIdentity: `web-${Date.now()}-${Math.random().toString(36).substring(2, 7)}`,
      participantName,
    });

    const resData = response.data?.data || response.data?.Data || response.data;
    const token = resData?.token || resData?.Token;
    const liveKitUrl = resData?.liveKitUrl || resData?.LiveKitUrl || import.meta.env.VITE_LIVEKIT_URL || 'wss://livekit.zaira.com.np';

    const room = new Room({
      adaptiveStream: true,
      dynacast: true,
      audioCaptureDefaults: {
        autoGainControl: true,
        echoCancellation: true,
        noiseSuppression: true,
      },
    });

    activeRooms.add(room);

    const attachTrack = (track) => {
      if (track && track.kind === Track.Kind.Audio) {
        try {
          const attachedElements = track.attach();
          const elements = Array.isArray(attachedElements) ? attachedElements : [attachedElements];
          elements.forEach((audioElement) => {
            if (audioElement) {
              audioElement.setAttribute('data-livekit-audio', 'true');
              audioElement.setAttribute('data-livekit-room', roomName);
              audioElement.autoplay = true;
              audioElement.volume = 1.0;
              audioElement.style.display = 'none';
              if (!document.body.contains(audioElement)) {
                document.body.appendChild(audioElement);
              }
              const playPromise = audioElement.play();
              if (playPromise !== undefined) {
                playPromise.catch((err) => {
                  console.warn('[LiveKitWebEngine] Autoplay play() retry:', err);
                  room.startAudio().catch(() => {});
                });
              }
            }
          });
          room.startAudio().catch(() => {});
        } catch (e) {
          console.warn('[LiveKitWebEngine] Error attaching audio track:', e);
        }
      }
    };

    // User gesture listener to unblock browser autoplay policy
    const unlockAudio = () => {
      room.startAudio().catch(() => {});
      document.querySelectorAll(`audio[data-livekit-room="${roomName}"]`).forEach((el) => {
        el.play().catch(() => {});
      });
    };

    activeAudioListeners.set(room, unlockAudio);
    document.addEventListener('click', unlockAudio);
    document.addEventListener('touchstart', unlockAudio);

    room.on(RoomEvent.Connected, async () => {
      try {
        await room.startAudio().catch(() => {});
        if (room.localParticipant) {
          await room.localParticipant.setMicrophoneEnabled(true);
        }
      } catch (e) {
        console.warn('[LiveKitWebEngine] Connected event mic enable warning:', e);
      }
    });

    room.on(RoomEvent.TrackPublished, (publication) => {
      try {
        publication.setSubscribed(true);
      } catch (e) {}
    });

    room.on(RoomEvent.TrackSubscribed, (track) => {
      attachTrack(track);
      room.startAudio().catch(() => {});
    });

    room.on(RoomEvent.TrackUnmuted, (publication) => {
      if (publication?.track) {
        attachTrack(publication.track);
      }
      room.startAudio().catch(() => {});
      document.querySelectorAll(`audio[data-livekit-room="${roomName}"]`).forEach((el) => {
        el.play().catch(() => {});
      });
    });

    room.on(RoomEvent.AudioPlaybackStatusChanged, () => {
      if (!room.canPlaybackAudio) {
        room.startAudio().catch(() => {});
      }
    });

    room.on(RoomEvent.ParticipantConnected, (participant) => {
      room.startAudio().catch(() => {});
      if (onParticipantConnected) {
        try {
          onParticipantConnected(participant);
        } catch (e) {}
      }
      if (participant?.trackPublications) {
        participant.trackPublications.forEach((pub) => {
          try {
            pub.setSubscribed(true);
            if (pub?.track) attachTrack(pub.track);
          } catch (e) {}
        });
      }
    });

    room.on(RoomEvent.DataReceived, async (payload, participant) => {
      try {
        const text = new TextDecoder().decode(payload);
        const message = JSON.parse(text);
        if (message?.type === 'HOLD') {
          const remoteHeld = Boolean(message.isHeld);
          if (room.localParticipant) {
            try {
              await room.localParticipant.setMicrophoneEnabled(!remoteHeld);
            } catch (e) {}
          }
          if (participant?.trackPublications) {
            participant.trackPublications.forEach((pub) => {
              if (pub?.kind === 'audio' || pub?.track?.kind === 'audio') {
                try {
                  pub.setSubscribed(!remoteHeld);
                } catch (e) {}
              }
            });
          }
        }
      } catch (e) {
        console.warn('[LiveKitWebEngine] Error parsing DataReceived:', e);
      }
    });

    room.on(RoomEvent.TrackUnsubscribed, (track) => {
      try {
        if (typeof track.detach === 'function') {
          track.detach().forEach((el) => el?.remove?.());
        }
      } catch (e) {}
    });

    room.on(RoomEvent.Disconnected, () => {
      disconnectLiveKitRoom(room).catch(() => {});
    });

    await room.connect(liveKitUrl, token);
    await room.startAudio().catch(() => {});
    try {
      if (room.localParticipant) {
        await room.localParticipant.setMicrophoneEnabled(true);
      }
    } catch (e) {
      console.warn('[LiveKitWebEngine] Immediate mic enable warning:', e);
    }

    // Attach audio tracks for participants who were already in the room when we connected
    room.remoteParticipants.forEach((participant) => {
      if (onParticipantConnected) {
        try {
          onParticipantConnected(participant);
        } catch (e) {}
      }
      if (participant?.trackPublications) {
        participant.trackPublications.forEach((pub) => {
          try {
            pub.setSubscribed(true);
            if (pub?.track) attachTrack(pub.track);
          } catch (e) {}
        });
      }
    });

    return room;
  } catch (error) {
    console.error('Failed to connect Web LiveKit call:', error);
    throw error;
  }
}

export async function holdLiveKitCall(room, isHeld) {
  if (room) {
    // 1. Mute/unmute local microphone
    if (room.localParticipant) {
      try {
        await room.localParticipant.setMicrophoneEnabled(!isHeld);
      } catch (e) {}
    }

    // 2. Mute/unmute remote audio subscriptions locally
    if (room.remoteParticipants) {
      room.remoteParticipants.forEach((participant) => {
        if (participant?.trackPublications) {
          participant.trackPublications.forEach((pub) => {
            if (pub?.kind === 'audio' || pub?.track?.kind === 'audio') {
              try {
                pub.setSubscribed(!isHeld);
              } catch (e) {}
            }
          });
        }
      });
    }

    // 3. Publish reliable HOLD data packet to room participants
    if (room.localParticipant) {
      try {
        const payload = new TextEncoder().encode(JSON.stringify({ type: 'HOLD', isHeld }));
        await room.localParticipant.publishData(payload, { reliable: true });
      } catch (e) {
        console.warn('[LiveKitWebEngine] Error publishing HOLD packet:', e);
      }
    }
  }
}

export async function transferLiveKitCall(room, targetId) {
  if (room?.localParticipant) {
    const payload = new TextEncoder().encode(JSON.stringify({ type: 'TRANSFERRING', targetDepartment: targetId }));
    await room.localParticipant.publishData(payload, { reliable: true });
  }
}

export async function disconnectLiveKitRoom(room) {
  if (!room) return;

  activeRooms.delete(room);

  // Remove gesture listener
  const listener = activeAudioListeners.get(room);
  if (listener) {
    document.removeEventListener('click', listener);
    document.removeEventListener('touchstart', listener);
    activeAudioListeners.delete(room);
  }

  // 1. Stop and unpublish local microphone tracks completely so hardware mic turns OFF
  if (room.localParticipant) {
    try {
      await room.localParticipant.setMicrophoneEnabled(false).catch(() => {});
    } catch (e) {}

    if (room.localParticipant.trackPublications) {
      room.localParticipant.trackPublications.forEach((pub) => {
        try {
          if (pub.track) {
            pub.track.stop();
            if (typeof pub.track.detach === 'function') {
              pub.track.detach().forEach((el) => el?.remove?.());
            }
            if (pub.track.mediaStreamTrack) {
              pub.track.mediaStreamTrack.stop();
            }
          }
        } catch (e) {}
      });
    }
  }

  // 2. Stop and detach remote tracks
  if (room.remoteParticipants) {
    room.remoteParticipants.forEach((participant) => {
      if (participant.trackPublications) {
        participant.trackPublications.forEach((pub) => {
          try {
            if (pub.track && typeof pub.track.detach === 'function') {
              pub.track.detach().forEach((el) => el?.remove?.());
            }
          } catch (e) {}
        });
      }
    });
  }

  // 3. Remove all audio elements for this room
  cleanupAudioElements(room.name);

  // 4. Remove all listeners and disconnect
  try {
    room.removeAllListeners();
    await room.disconnect().catch(() => {});
  } catch (e) {
    console.warn('Error disconnecting LiveKit room:', e);
  }
}
